import {
	Button,
	type ColumnDef,
	DataTable,
	SearchInput,
	useUrlPagination,
} from "@monbits/ui";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import CollapseString from "src/components/collapse-string/collapse-string";
import DrawerForm from "src/components/drawer-form/drawer-form";
import TimeLabel from "src/components/time-label/time-label";
import customersQueries from "src/queries/customer-queries";
import type { Customer } from "src/types";

const CustomersPage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const search = searchParams.get("search") || "";

	const listCustomers = customersQueries.listCustomers();

	const [openedCustomer, setOpenedCustomer] = React.useState<Customer | null>(
		null,
	);
	const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = React.useState(false);

	const customers = React.useMemo(() => {
		const allCustomers =
			listCustomers.data?.pages.flatMap((page) => page.results) || [];

		if (!search) {
			return allCustomers;
		}

		const searchLower = search.toLowerCase();
		return allCustomers.filter(
			(customer) =>
				customer.email.toLowerCase().includes(searchLower) ||
				customer.id.toLowerCase().includes(searchLower),
		);
	}, [listCustomers.data, search]);

	const { page, limit, setPage, setLimit } = useUrlPagination({
		totalCount: customers.length,
	});

	const paginatedCustomers = React.useMemo(() => {
		const offset = (page - 1) * limit;
		return customers.slice(offset, offset + limit);
	}, [customers, page, limit]);

	const isLoading = listCustomers.isLoading || listCustomers.isFetching;

	const handleViewCustomer = React.useCallback((customer: Customer) => {
		setOpenedCustomer(customer);
		setIsCustomerDrawerOpen(true);
	}, []);

	const columns: ColumnDef<Customer>[] = React.useMemo(
		() => [
			{
				id: "createdAt",
				header: "Created At",
				cell: (row) => <TimeLabel time={row.createdAt} />,
			},
			{
				id: "email",
				header: "Email",
				cell: (row) => (
					<CollapseString text={row.email} collapseAt={32} withPopover />
				),
			},
		],
		[],
	);

	return (
		<>
			<div>
				<div className="mb-4">
					<SearchInput
						placeholder="Search by email or ID..."
						className="w-full sm:w-[300px]"
					/>
				</div>

				<DataTable
					columns={columns}
					data={paginatedCustomers}
					isLoading={isLoading}
					onRowClick={handleViewCustomer}
					getRowId={(row) => row.id}
					emptyMessage={search ? "No results found" : "No customers yet"}
					emptyDescription={
						search
							? "No customers match your search criteria"
							: "Customers appear here automatically after their first successful payment."
					}
					emptyAction={
						search ? undefined : (
							<Button variant="outline" onClick={() => navigate("/payments")}>
								Go to payments
							</Button>
						)
					}
					pagination={
						customers.length > 0
							? {
									page,
									pageSize: limit,
									totalCount: customers.length,
									onPageChange: setPage,
									onPageSizeChange: setLimit,
								}
							: undefined
					}
				/>

				<DrawerForm
					title="Customer details"
					isOpen={isCustomerDrawerOpen}
					onClose={() => setIsCustomerDrawerOpen(false)}
					width={512}
				>
					{openedCustomer && (
						<div className="space-y-4">
							<div className="grid grid-cols-1 gap-4">
								<div>
									<p className="text-sm text-muted-foreground">Email</p>
									<p className="font-medium">{openedCustomer.email}</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Customer ID</p>
									<p className="font-mono text-sm">{openedCustomer.id}</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Created At</p>
									<TimeLabel time={openedCustomer.createdAt} />
								</div>
							</div>
						</div>
					)}
				</DrawerForm>
			</div>

			<Outlet />
		</>
	);
};

export default CustomersPage;
